import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { RoutineReminder, RoutineBlock } from '../utils/routineStorage'

interface GentleReminderProps {
  reminder: RoutineReminder
  routine: RoutineBlock
  onStart: () => void
  onNotNow: () => void
  onSkipToday: () => void
  onPauseReminders: () => void
}

const GentleReminder = ({ 
  reminder, 
  routine, 
  onStart, 
  onNotNow, 
  onSkipToday, 
  onPauseReminders 
}: GentleReminderProps) => {
  const [isVisible, setIsVisible] = useState(true)
  const [showOptions, setShowOptions] = useState(false)

  const handleClose = (action: () => void) => {
    setIsVisible(false)
    setTimeout(action, 300)
  }

  const getReminderMessage = () => {
    const timeOfDay = routine.time_range
    const skipCount = reminder.skip_count
    
    if (skipCount >= 2) {
      return {
        title: "Would you like to pause this reminder for now?",
        message: "I notice you've been skipping this routine. That's completely okay. Would you like me to pause these reminders for a while?",
        isGentle: true
      }
    }
    
    switch (timeOfDay) {
      case 'morning':
        return {
          title: "It's a quiet moment for your morning routine",
          message: `Would you like to start your "${routine.name}" routine? No pressure at all.`,
          isGentle: false
        }
      case 'afternoon':
        return {
          title: "A gentle pause for your afternoon routine",
          message: `Time for "${routine.name}" if that feels right today.`,
          isGentle: false
        }
      case 'evening':
        return {
          title: "Evening routine time",
          message: `Your "${routine.name}" routine is ready when you are.`,
          isGentle: false
        }
      default:
        return {
          title: "Gentle reminder",
          message: `Would you like to do your "${routine.name}" routine?`,
          isGentle: false
        }
    }
  }

  const message = getReminderMessage()

  if (!isVisible) return null

  return (
    <div className="fixed top-12 sm:top-20 left-4 sm:left-8 right-4 sm:right-8 z-30 transition-all duration-300">
      <div className="bg-sage-50/90 backdrop-blur-sm rounded-3xl border border-sage-100/50 p-4 sm:p-6 max-w-sm mx-auto shadow-lg">
        <div className="text-center mb-4 sm:mb-6">
          <div className="w-12 h-12 mx-auto mb-3 sm:mb-4 bg-sage-100/60 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h3 className="text-sage-600 font-light text-sm sm:text-base mb-2 sm:mb-3">
            {message.title}
          </h3>
          
          <p className="text-sage-500 font-light text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
            {message.message}
          </p>
        </div>

        {message.isGentle ? (
          // Special handling for pause suggestion
          <div className="space-y-3">
            <button
              onClick={() => handleClose(onPauseReminders)}
              className="w-full px-4 py-3 bg-sage-100/60 hover:bg-sage-200/50 
                         text-sage-600 rounded-2xl text-xs sm:text-sm font-light border border-sage-200/40 hover:border-sage-300/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50"
            >
              Pause reminders for now
            </button>
            
            <button
              onClick={() => handleClose(onStart)}
              className="w-full px-4 py-3 bg-mint-50/60 hover:bg-mint-100/50 
                         text-mint-600 rounded-2xl text-xs sm:text-sm font-light border border-mint-100/40 hover:border-mint-200/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-mint-100/50"
            >
              Actually, let's try it
            </button>
            
            <button
              onClick={() => handleClose(onNotNow)}
              className="w-full px-4 py-3 bg-sage-50/40 hover:bg-sage-100/40 
                         text-sage-500 rounded-2xl text-xs sm:text-sm font-light border border-sage-100/30 hover:border-sage-200/40
                         transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50"
            >
              Not today
            </button>
          </div>
        ) : (
          // Normal reminder options
          <div className="space-y-3">
            <button
              onClick={() => handleClose(onStart)}
              className="w-full px-4 py-3 bg-sage-100/60 hover:bg-sage-200/50 
                         text-sage-600 rounded-2xl text-xs sm:text-sm font-light border border-sage-200/40 hover:border-sage-300/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50"
            >
              Start now
            </button>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={() => handleClose(onNotNow)}
                className="flex-1 px-4 py-3 bg-cream-50/60 hover:bg-cream-100/50 
                           text-cream-600 rounded-2xl text-xs sm:text-sm font-light border border-cream-100/40 hover:border-cream-200/50
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100/50"
              >
                Not now
              </button>
              
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="sm:flex-none px-3 py-3 bg-neutral-50/60 hover:bg-neutral-100/50 
                           text-neutral-500 rounded-2xl border border-neutral-100/40 hover:border-neutral-200/50
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-neutral-100/50"
              >
                <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
            </div>
            
            {showOptions && (
              <div className="pt-2 sm:pt-3 border-t border-sage-100/30 space-y-2">
                <button
                  onClick={() => handleClose(onSkipToday)}
                  className="w-full px-4 py-2 bg-neutral-50/40 hover:bg-neutral-100/40 
                             text-neutral-500 rounded-xl text-xs font-light border border-neutral-100/30 hover:border-neutral-200/40
                             transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-neutral-100/50"
                >
                  Skip today
                </button>
                
                <button
                  onClick={() => handleClose(onPauseReminders)}
                  className="w-full px-4 py-2 bg-neutral-50/40 hover:bg-neutral-100/40 
                             text-neutral-500 rounded-xl text-xs font-light border border-neutral-100/30 hover:border-neutral-200/40
                             transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-neutral-100/50"
                >
                  Pause this reminder
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gentle fade-in animation */}
      <style>{`
        @keyframes gentleFadeIn {
          from { 
            opacity: 0; 
            transform: translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        .fixed {
          animation: gentleFadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  )
}

export default GentleReminder